<template>
    <NuxtLayout>
        <NuxtPage />
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {onBeforeMount, onBeforeUnmount, provide, ref, watch} from 'vue';
    import type {PrismicDocument} from '@prismicio/types';
    import type {HostSettingsResource} from '#imports';
    import {storeToRefs, useAsyncData, useAuth, useHostSettings, useLocale, usePrismicClient, usePrismicPreviewScript} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import type {ResourcesCenterHeaderDocument} from '@malt/nuxt-prismic-module/prismicio-types';
    import {usePageCache} from '@malt/utils';
    import {HOST_SETTINGS, MEDIA_BREAKPOINTS, PRISMIC_CLIENT, PRISMIC_DOCUMENT, RC_NAVBAR, USER_LOCALE} from '~/app/keys';
    import usePrismicHeader from '~/composables/content/usePrismicHeader';
    import {useBreakpointsHelper} from '~/composables/useBreakpointsHelpers';

    const enablePageCache = useFeatureFlag('enable-page-cache');
    const authStore = useAuth();
    const {loggedIn} = storeToRefs(authStore);

    usePrismicPreviewScript();

    const {locale} = useLocale();
    provide(USER_LOCALE, locale.value);

    const breakpoints = ref(useBreakpointsHelper());
    provide(MEDIA_BREAKPOINTS, breakpoints);

    const document = ref<PrismicDocument>();
    provide(PRISMIC_DOCUMENT, document);

    const client = usePrismicClient();
    provide(PRISMIC_CLIENT, client);

    const {data: navbarDocument} = await useAsyncData('navbar', () => usePrismicHeader(client));
    provide(RC_NAVBAR, navbarDocument.value as ResourcesCenterHeaderDocument);

    function handleResize() {
        breakpoints.value = useBreakpointsHelper();
    }
    const {data: hostSettings} = await useAsyncData('host-settings', () => useHostSettings());
    provide(HOST_SETTINGS, hostSettings.value as HostSettingsResource);

    if (!loggedIn.value && enablePageCache.value) {
        usePageCache();
    }

    watch([document], () => {
        try {
            if (window) {
                window.dispatchEvent(new CustomEvent('Navigation'));
            }
        } catch (_error) {}
    });

    onBeforeMount(() => {
        window.addEventListener('resize', handleResize);
    });
    onBeforeUnmount(() => {
        window.removeEventListener('resize', handleResize);
    });
</script>
